import { Nugget } from '@deltek/specification-client/core/Models/nugget'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectNugget } from '@deltek/specification-client/core/Models/projectNugget'
import { ProjectUniformatClassification } from '@deltek/specification-client/core/Models/projectUniformatClassification'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { UniformatClassification } from '@deltek/specification-client/core/Models/uniformatClassification'
import {
    GetNuggetResponse,
    GetProjectNuggetResponse,
    ImportedManufacturersAndBOD,
    NuggetWithSpecAgentId,
    ProjectManufacturerProductDetailOnFamily,
    ProjectNuggetWithSpecAgentId,
} from 'api/graphQL/types/projectElement.types'
import { ProjectElementMetadata } from 'models/projectElements'
import { ProjectSettings } from 'models/projectSettings'
import {
    PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES,
    PART_TYPES,
} from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { Key } from 'react'
import { ContentItemViewNodesType } from './contentItemViewNode.type'

export interface ProductFamilyView {
    id: string
    prefix: string
    familyId: string
    delimiter: string
    name: string
    nuggets: Partial<Nugget>[]
    contentItemViewNodes: Partial<ContentItemViewNodesType>[]
}

export interface GetProductFamilyViewResponse {
    productFamilyView: Partial<ProductFamilyView>
}

export interface ProjectProductFamilyViewWithPTP
    extends ProjectProductFamilyView {
    familyAddedProductTypes?: ProjectElementNode[]
    familyProductListings?: Partial<ProjectManufacturerProductDetailOnFamily>[]
    familyCustomManufacturerProducts?: Partial<
        ProjectManufacturerProductDetailOnFamily
    >[]
    familyManufacturerImportedManufacturer?: Partial<
        ImportedManufacturersAndBOD
    >[]
    familyBodImportedManufacturer?: Partial<ImportedManufacturersAndBOD>[]
}

export interface GetProjectProductFamilyViewResponse {
    getProjectProductFamilyView: Partial<ProjectProductFamilyViewWithPTP>
}

export interface ProjectProductFamilyView {
    id: string
    prefix: string
    familyId: string
    delimiter: string
    name: string
    projectNuggets: Partial<ProjectNugget>[]
    projectElementMetadata: Partial<ProjectElementMetadata>
    hasProductType: boolean
    contentItemViewNodes: Partial<ContentItemViewNodesType>[]
    firmId: string
    notesCount: number
}

export interface GetNuggetContentViewResponse extends GetNuggetResponse {
    nugget: Partial<NuggetWithSpecAgentId> & {
        contentItemViewNodes: Partial<ContentItemViewNodesType>[]
    }
}

export interface GetProjectNuggetContentViewResponse
    extends GetProjectNuggetResponse {
    getProjectNugget: Partial<ProjectNuggetWithSpecAgentId> & {
        contentItemViewNodes: Partial<ContentItemViewNodesType>[]
    }
}

export type NodeType = typeof NODE_TYPES[keyof typeof NODE_TYPES]

export type PartType = typeof PART_TYPES[keyof typeof PART_TYPES]

/**
 * Represents a node in the hierarchical tree data structure
 * TODO: Add more properties as needed to render the tree structure
 * */
export type DataNode<T = Partial<TextContentElement>> = {
    key: string
    type: NodeType
    contentItemNodes: T[]
    order: number
    rootArticleId?: string
    children?: DataNode<T>[]
    specifierNotes?: string[]
    parentComments?: string[]
    expanded?: boolean
    isHidden?: boolean
    isLeaf?: boolean
    isProductGroup?: boolean
    isProductType?: boolean
    isUserAdded?: boolean
    isProductTypeContent?: boolean
    isManufacturers?: boolean
    isBOD?: boolean
}

export interface ElementItemNode {
    id: string
    actionFlag: string
    elementId: string
    elementType: string
    isProductTypeProject: boolean
    isProjectProductFamily: boolean
    baseElementId?: string
    /** the elementId used for update mutation (PT level) */
    parentFamilyId?: string
}

export interface AssignedUserList {
    userName: string
    userId?: string | null
}

/** Represents the props for the workspace editor component */
export interface WorkspaceEditorProps {
    /** The current selected project element node from element tree */
    selectedProjectElementNode?: Partial<ProjectElementNode> | null
    /** The current project uniformat classification data */
    selectedElementData?:
        | Partial<ProjectUniformatClassification>
        | Partial<UniformatClassification>
    /** The projectId currently loaded in the workspace */
    projectId: string
    /** The flag to indicate if the element tree is currently loading */
    isElementTreeLoading?: boolean
    /** The flag to indicate if the product family is currently loading */
    isTrackChangeToggleOn?: boolean
    insertNewProductTypeElement?: (
        element: ProjectElementNode,
        isSelectedFromListingDialog?: boolean,
        newNodesForAdditionalInstances?: ProjectElementNode[]
    ) => void
}

/** Represents the global actions available for the workspace */
export interface Workspace2Actions {
    /** sets whether the content is from OM/SMM */
    setIsContentTeamFromStudioPage: (
        isContentTeamFromStudioPage: boolean
    ) => void
    /** sets the tree data pre-processed in tree data mapper */
    setTreeData: (treeData: DataNode[]) => void
    /** sets whether text is currently being edited in PlainText */
    setIsEditingPlainText: (isEditing: boolean) => void
    /** sets the selected project element node from element tree */
    setSelectedProjectElementNode: (
        selectedProjectElementNode: Partial<ProjectElementNode>
    ) => void
    /** sets the active tab */
    setActiveTab: (activeTab: number) => void
    /** sets the articles loading state */
    setArticlesLoading: (keys: string[]) => void
    /** sets the expanded keys */
    setExpandedKeys: (keys: React.Key[]) => void
    /** sets the selected project element nodes */
    setContentItemViewNodes: (
        contentItemViewNodes: Partial<ContentItemViewNodesType>[]
    ) => void
    /** sets the previous tree content item view nodes */
    setPreviousContentItemViewNodes: (
        previousContentItemViewNodes: Partial<ContentItemViewNodesType>[]
    ) => void
    /** sets the current project uniformat classification data */
    setClassificationNodes: (classificationNodes: DataNode[]) => void
    /** sets the currently being added text content element */
    setTextContentElementToAdd: (
        element: Partial<TextContentElement> | null
    ) => void
    /** switches the workspace between enabled and disabled states */
    switchWorkspace: (enabled: boolean) => void
    /** sets the maintenance mode and stores the associated error */
    setMaintenanceMode: (error: Error | null) => void
    /** sets the workspace content to the default state (reset) */
    setDefault: () => void
    /** sets the track changes sidebar state */
    setIsTrackChangesExpanded?: (isTrackChangesExpanded: boolean) => void
    /** sets the status of tree loading or not */
    setIsTreeLoading: (isLoading: boolean) => void
    /** sets the project settings */
    setProjectSettings: (projectSettings: ProjectSettings) => void
}

/** Represents the state and actions for workspace 2.0 management */
export interface Workspace2Store {
    /** indicates whether the content is from OM/SMM */
    isContentTeamFromStudioPage: boolean
    /** stores the tree data pre-processed in tree data mapper */
    treeData: DataNode[]
    /** indicates whether text is currently being edited in PlainText */
    isEditingPlainText: boolean
    /** indicates whether the beta workspace is currently enabled */
    isBetaWorkspaceEnabled?: boolean
    /** indicates whether the system is currently in maintenance mode */
    isUnderMaintenance?: boolean
    /* stores the status of tree loading or not*/
    isTreeLoading: boolean
    /** stores the most recent error that triggered maintenance mode */
    lastError?: Error | null
    /** stores the currently selected project element node from elementTree */
    selectedProjectElementNode: Partial<ProjectElementNode>
    /** stores the currently selected project element node id from elementTree */
    selectedProjectElementNodeId: string
    /** stores the current active tab index (non-zero based) */
    activeTab: number
    /** stores the keys of nodes that are currently being loaded */
    articlesLoading: string[]
    /** stores the keys of nodes that are currently expanded */
    expandedKeys: Key[]
    /** stores the selected tree content item view nodes */
    contentItemViewNodes: Partial<ContentItemViewNodesType>[]
    /** indicates whether the track changes sidebar is expanded or not */
    isTrackChangesExpanded?: boolean
    /** stores the previous tree content item view nodes */
    previousContentItemViewNodes?: Partial<ContentItemViewNodesType>[]
    /** stores the current project uniformat classification data */
    classificationNodes?: DataNode[]
    /** stores the currently being added text content element */
    textContentElementToAdd: Partial<TextContentElement> | null
    /** stores the current project settings */
    projectSettings: ProjectSettings
    /** stores the actions available for the workspace */
    actions: Workspace2Actions
}

/** Represents the state and actions for workspace 2.0 management */
export interface ElementItemStoreType {
    /** stores the selected element item value */
    elementItemValue: ElementItemNode
    /** sets the selected element item value */
    setElementItemValue: (elem: ElementItemNode) => void
}

export interface ProjectPropertiesStoreType {
    assignedUsersList: AssignedUserList[]
    setAssignedUsersList: (users: AssignedUserList[]) => void
}

export interface LeftMostPaneStoreType {
    /** Determines whether the leftmost pane is visible. */
    showLeftMostPane: boolean
    /** Whether the element panel state should be updated. */
    updateElementPanelState?: boolean

    /** Determines whether the search bar is visible. */
    isSearchBarDisplayed?: boolean

    /** The current active pane identifier. */
    currentPane: string

    /** Handles collapsing the left panel. */
    setshowLeftMostPane?: (value: boolean) => void

    /** Sets the current active pane. */
    setCurrentPane: (value: string) => void

    /** Updates the element panel state. */
    setUpdateElementPanelState?: (value: boolean) => void

    /** Handles show/hide the search bar */
    setIsSearchBarDisplayed?: (value: boolean) => void
}

export enum FileType {
    PDF = 'PDF',
    DOCX = 'DOCX',
}
export interface ActionBarActions {
    setIsExportLoading: (type: FileType, loading: boolean) => void
    setIsSupportingDocsLoading: (loading: boolean) => void
    setExportUrl: (type: string, url: string) => void
    setSupportingDocsUrl: (url: string) => void
}

export interface ActionBarStoreType {
    isPDFExportLoading: boolean
    isWordExportLoading: boolean
    isSupportingDocsLoading: boolean
    pdfExportUrl: string
    wordExportUrl: string
    supportingDocsUrl: string
    actions: ActionBarActions
}

export interface ScrollableTreeRef {
    scrollTo: (options: {
        key: string | number
        align?: 'top' | 'bottom' | 'auto'
        offset?: number
    }) => void
}
